import { useRef, useState } from "react";
import { FileDown, Settings2, Upload, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { uploadAsset } from "./Assets";
import { useAuth } from "../context/AuthContext";
import { api, fileUrl, handleErr } from "../lib/api";

const AgencyDialog = ({ open, onClose }) => {
  const { user, refresh } = useAuth();
  const a = user?.agency || {};
  const [f, setF] = useState({ name: a.name || user?.name || "", website: a.website || "", email: a.email || "" });
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const onLogo = async (file) => {
    if (!file) return;
    try { await uploadAsset(file, { kind: "agency" }); await refresh(); toast.success("Agency logo saved"); } catch (e) { handleErr(e); }
  };
  const save = async () => {
    setBusy(true);
    try { await api.put("/me/agency", f); await refresh(); toast.success("Report branding saved"); onClose(); } catch (e) { handleErr(e); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F172A] border-slate-800 max-w-md max-h-[90vh] overflow-y-auto" data-testid="agency-branding-dialog">
        <DialogHeader><DialogTitle>Your agency branding</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400 -mt-2">Shown on client audit PDFs. A small "Powered by NamoAI" credit appears in the footer.</p>
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-lg bg-slate-900 border border-slate-800 grid place-items-center overflow-hidden">
            {a.logoAssetId ? <img data-testid="agency-logo-preview" src={fileUrl("asset", a.logoAssetId)} alt="agency logo" className="w-full h-full object-contain" /> : <ImagePlus size={20} className="text-slate-600" />}
          </div>
          <button data-testid="agency-logo-upload-btn" onClick={() => ref.current?.click()} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-slate-700 hover:border-orange-500 hover:text-orange-400 transition-colors"><Upload size={13} />{a.logoAssetId ? "Replace logo" : "Upload agency logo"}</button>
          <input ref={ref} data-testid="agency-logo-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { onLogo(e.target.files[0]); e.target.value = ""; }} />
        </div>
        {[["name", "Agency name"], ["website", "Agency website"], ["email", "Contact email"]].map(([k, l]) => (
          <label key={k} className="block space-y-1.5"><span className="label-mono">{l}</span><Input data-testid={`agency-${k}-input`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={l} /></label>
        ))}
        <DialogFooter><Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button><Button data-testid="agency-save-btn" disabled={busy || !f.name.trim()} onClick={save} className="rounded-full">{busy ? "Saving..." : "Save branding"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AuditPdfActions = ({ brand }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/brands/${brand.brandId}/audit/pdf`, { responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "website-audit.pdf";
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data);
      link.download = name;
      link.click();
    } catch (e) {
      if (e.response?.data instanceof Blob) e.response.data = JSON.parse(await e.response.data.text());
      handleErr(e);
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="audit-pdf-actions">
      <Button data-testid="audit-pdf-download-btn" size="sm" disabled={busy} onClick={download} className="rounded-full"><FileDown size={14} className="mr-1.5" />{busy ? "Building PDF..." : "Download client PDF"}</Button>
      <Button data-testid="audit-branding-btn" size="sm" variant="outline" onClick={() => setOpen(true)} className="rounded-full"><Settings2 size={14} className="mr-1.5" />Report branding</Button>
      {open && <AgencyDialog open={open} onClose={() => setOpen(false)} />}
    </div>
  );
};
